/* ==========================================================
   Shared AI block base classes.
   Each block's inline <script> registers a sub-class via:
     customElements.define('tag-name', class extends window.AISizeGuide {});
   Must be reached via `window.*` because class declarations in
   classic scripts don't auto-attach to the global object.
   ========================================================== */

window.AISizeGuide = class AISizeGuide extends HTMLElement {
  connectedCallback() {
    this.toggleButtons = this.querySelectorAll('.ai-size-guide__toggle-button');
    this.tableWrappers = this.querySelectorAll('.ai-size-guide__table-wrapper');
    this.slider = this.querySelector('.ai-size-guide__toggle-slider');
    this.contentContainer = this.querySelector('.ai-size-guide__content');
    this.scrollIndicator = this.querySelector('.ai-size-guide__scroll-indicator-thumb');
    this.scrollTrack = this.querySelector('.ai-size-guide__scroll-indicator-track');

    this.setupEventListeners();
    this.updateSliderPosition();
    this.setContentHeight();
    this.normalizeTableStructure();
    this.setupScrollIndicator();
  }

  setupEventListeners() {
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const unit = e.target.getAttribute('data-unit');
        this.switchUnit(unit);
      });
    });
  }

  normalizeTableStructure() {
    this.tableWrappers.forEach((wrapper) => {
      const table = wrapper.querySelector('table');
      if (table) {
        const rows = table.querySelectorAll('tr');
        let maxCells = 0;
        rows.forEach((row) => {
          const cells = row.querySelectorAll('th, td');
          if (cells.length > maxCells) maxCells = cells.length;
        });
        rows.forEach((row) => {
          const cells = row.querySelectorAll('th, td');
          cells.forEach((cell) => {
            cell.style.width = (100 / maxCells) + '%';
          });
        });
      }
    });
  }

  switchUnit(unit) {
    this.toggleButtons.forEach((button) => {
      const buttonUnit = button.getAttribute('data-unit');
      if (buttonUnit === unit) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
    this.tableWrappers.forEach((wrapper) => {
      if (wrapper.getAttribute('data-unit') === unit) {
        wrapper.classList.add('active');
      } else {
        wrapper.classList.remove('active');
      }
    });
    this.updateSliderPosition();
    this.updateScrollIndicator();
  }

  updateSliderPosition() {
    const activeButton = this.querySelector('.ai-size-guide__toggle-button.active');
    if (activeButton && this.slider) {
      const buttonRect = activeButton.getBoundingClientRect();
      const containerRect = activeButton.parentElement.getBoundingClientRect();
      const offsetLeft = buttonRect.left - containerRect.left;
      const size = buttonRect.width;
      this.slider.style.width = size + 'px';
      this.slider.style.transform = 'translateX(' + offsetLeft + 'px)';
    }
  }

  setupScrollIndicator() {
    if (!this.contentContainer || !this.scrollIndicator || !this.scrollTrack) return;
    const update = () => this.updateScrollIndicator();
    this.contentContainer.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    requestAnimationFrame(update);
    setTimeout(update, 50);
  }

  updateScrollIndicator() {
    const el = this.contentContainer;
    const thumb = this.scrollIndicator;
    const track = this.scrollTrack;
    if (!el || !thumb || !track) return;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;
    if (scrollWidth <= clientWidth + 1) {
      track.style.opacity = '0';
      return;
    }
    track.style.opacity = '1';
    const maxScroll = scrollWidth - clientWidth;
    const trackWidth = track.clientWidth;
    const ratio = clientWidth / scrollWidth;
    const thumbWidth = Math.max(24, Math.round(trackWidth * ratio));
    thumb.style.width = thumbWidth + 'px';
    const maxThumbX = trackWidth - thumbWidth;
    const progress = maxScroll > 0 ? (el.scrollLeft / maxScroll) : 0;
    thumb.style.transform = 'translateX(' + Math.round(maxThumbX * progress) + 'px)';
  }

  setContentHeight() {
    if (!this.contentContainer) return;
    let maxHeight = 0;
    this.tableWrappers.forEach((wrapper) => {
      const table = wrapper.querySelector('table');
      if (table) {
        const height = table.offsetHeight;
        if (height > maxHeight) maxHeight = height;
      }
    });
    if (maxHeight > 0) {
      this.contentContainer.style.minHeight = maxHeight + 'px';
    }
  }
};
